import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, Mic, FolderOpen, BarChart3, Download } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Subscribe = () => {
  const { user, subscriptionInfo, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    refreshUser();
    // Read billing period from URL params or localStorage
    const urlBilling = searchParams.get('billing');
    const storedBilling = localStorage.getItem('billingPeriod');
    const billing = urlBilling || storedBilling || 'monthly';
    setBillingPeriod(billing);
    // Clear localStorage after reading
    if (storedBilling) {
      localStorage.removeItem('billingPeriod');
    }
  }, [searchParams, refreshUser]);

  const handleSubscribe = async () => {
    if (loading) return; // Prevent double calls
    
    setLoading(true);
    try {
      const origin = window.location.origin;
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        origin_url: origin,
        billing_period: billingPeriod
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      setLoading(false);
    }
  };

  // Calculate prices
  const monthlyPrice = 2.99;
  const yearlyPricePerMonth = 2.39;
  const yearlyTotalPrice = yearlyPricePerMonth * 12; // $28.68
  
  const displayPrice = billingPeriod === 'yearly' ? yearlyTotalPrice : monthlyPrice;
  const displayPeriod = billingPeriod === 'yearly' ? '/year' : '/month';
  const billingText = billingPeriod === 'yearly' 
    ? 'Billed annually. Auto-renews. You can cancel anytime.' 
    : 'Billed monthly. Auto-renews. You can cancel anytime.';

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)' }}>
      <Card className="w-full max-w-2xl bg-white shadow-2xl border-0" data-testid="subscribe-card">
        <div className="p-8 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center shadow-xl">
              <Mic className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Space Grotesk' }} data-testid="subscribe-title">
            {subscriptionInfo?.status === 'trial' ? 'Upgrade to Pro' : 'Continue Tracking Your Time'}
          </h1>
          
          {subscriptionInfo?.status === 'trial_expired' && (
            <p className="text-gray-600 mb-6">Your 7-day trial has ended</p>
          )}

          {/* Benefits */}
          <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-8 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Unlimited Voice Entries</h3>
                  <p className="text-sm text-gray-600">Log time with just your voice</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Unlimited Projects</h3>
                  <p className="text-sm text-gray-600">Organize across all your work</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">AI-Powered Notes</h3>
                  <p className="text-sm text-gray-600">Smart parsing and organization</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Export & Reports</h3>
                  <p className="text-sm text-gray-600">Get insights into your time</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-gradient-to-br from-cyan-500 to-teal-500 text-white rounded-2xl p-8 mb-6">
            <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }} data-testid="price">
              ${displayPrice.toFixed(2)}
            </div>
            <div className="text-2xl mb-4 opacity-90">{displayPeriod}</div>
            {billingPeriod === 'yearly' && (
              <div className="text-lg mb-2 opacity-90">${yearlyPricePerMonth.toFixed(2)}/month billed annually</div>
            )}
            <p className="text-cyan-100 text-sm">Cancel anytime • No hidden fees</p>
          </div>

          {/* CTA */}
          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold py-6 text-lg rounded-xl shadow-lg"
            data-testid="subscribe-button"
          >
            {loading ? 'Redirecting to checkout...' : 'Subscribe Now'}
          </Button>

          <p className="text-xs text-gray-500 mt-4">
            {billingText}
          </p>

          {subscriptionInfo?.status === 'trial' && subscriptionInfo.trial_days_left > 0 && (
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="mt-4"
              data-testid="back-home-button"
            >
              Back to Home ({subscriptionInfo.trial_days_left} days left)
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Subscribe;
